import {memo, useMemo, useState, useCallback} from 'react'

import {
  DEFAULT_BAR_WIDTH_WITH_MARGIN,
  CHART_PADDING,
  MIN_BAR_WIDTH_WITH_MARGIN,
} from '../TestHistoryChart.utils'

import type {Props} from './Chart.types'
import MainCanvas from './MainCanvas/MainCanvas'
import Preview from './Preview/Preview'
import type {Domain} from './Preview/Preview.types'

import styles from './Chart.css'

function Chart({className, data, width, withAdaptScale}: Props) {
  const dataCount = data.length
  const minX = 0
  const minY = 0
  const maxX = dataCount
  const maxY = useMemo(() => Math.max(...data.map(datum => datum.y)), [data])
  const mainCanvasWidth = width - CHART_PADDING.left - CHART_PADDING.right
  const maxDomainColumns = Math.floor(mainCanvasWidth / DEFAULT_BAR_WIDTH_WITH_MARGIN)
  const showPreview = dataCount > maxDomainColumns
  const [domain, setDomain] = useState<Domain>({
    x: showPreview ? [dataCount - maxDomainColumns, dataCount] : [0, dataCount],
    y: [minY, maxY],
  })
  const handleChangeDomain = useCallback(
    (d: Domain) => {
      if (mainCanvasWidth / (Number(d.x[1]) - Number(d.x[0])) > MIN_BAR_WIDTH_WITH_MARGIN) {
        setDomain(d)
      } else {
        setDomain({...domain, x: d.x})
      }
    },
    [domain, setDomain, mainCanvasWidth],
  )
  const maxDomain = useMemo(
    () => ({
      y: maxY,
      x: maxX,
    }),
    [maxY, maxX],
  )
  const minDomain = useMemo(
    () => ({
      y: minY,
      x: minX,
    }),
    [minY, minX],
  )
  return (
    <div className={className}>
      <div className={styles.chart}>
        {showPreview && (
          <Preview
            maxDomain={maxDomain}
            minDomain={minDomain}
            data={data}
            width={width}
            domain={domain}
            onDomainChange={handleChangeDomain}
          />
        )}
        <MainCanvas
          maxDomain={maxDomain}
          minDomain={minDomain}
          data={data}
          width={width}
          domain={domain}
          onDomainChange={handleChangeDomain}
          withAdaptScale={withAdaptScale}
        />
      </div>
    </div>
  )
}

export default memo<Props>(Chart)
